import CONSTANTS from '@/constants'
import { IProductExtended } from '@/@types'

const DEFAULT_SUFFIX = ' - Checkout | INE'
export const DEFAULT_CHECKOUT_TITLE = 'Checkout - INE Cybersecurity, Networking & Cloud Training'

const ROUTE_TITLE_OVERRIDES: Record<string, string> = {
  account: 'Create Account - Checkout | INE',
  payment: 'Payment Details - Checkout | INE',
  confirmation: 'Order Confirmation - Checkout | INE',
  complete: 'Order Complete - Checkout | INE',
}

const {
  PLANS,
  VOUCHERS,
  BLACK_FRIDAY_PRODUCT_BUNDLES,
  LEAP_YEAR_PROMO_PRODUCT_BUNDLES,
  TCS_PROMO_PRODUCT_BUNDLES,
  SUMMER_SAVINGS_BUNDLE,
} = CONSTANTS

const PRODUCT_TITLE_OVERRIDES: Record<string, string> = {
  [PLANS.ANNUAL]: 'INE Fundamentals Annual Plan',
  [PLANS.MONTHLY]: 'INE Fundamentals Monthly Plan',
  [PLANS.MONTHLY_EJPT]: 'INE eJPT Monthly Plan',
  [PLANS.PREMIUM]: 'INE Premium Plan',
  [PLANS.PREMIUM_MONTHLY]: 'INE Premium Monthly Plan',
  [PLANS.PREMIUM2YR]: 'INE Premium 2-Year Plan',
  [PLANS.PREMIUM_PLUS]: 'INE Premium Plus Plan',
  [PLANS.INE_PREMIUM_BOOGO_2Y]: 'INE Premium 2-Year BOOGO Plan',
  [PLANS.INE_PREMIUM_IND_BOOGO_2Y]: 'INE Premium 2-Year BOOGO Plan',
  [PLANS.LEGACY]: 'INE Legacy Streaming Plan',
  [PLANS.BAH]: 'INE Premium Plus BAH Plan',
  [PLANS.EJPT_FUNDAMENTALS]: 'INE eJPT Fundamentals Bundle',
  [PLANS.EWPT_PREMIUM]: 'INE eWPT Certification Bundle',
  [PLANS.ECIR_PREMIUM]: 'INE ECIR Certification Bundle',
  [PLANS.EJDS_PREMIUM]: 'INE EJDS Certification Bundle',
  [PLANS.ESOC_PREMIUM]: 'INE eSOC Certification Bundle',
  [PLANS.EAIS_PREMIUM]: 'INE eAIS Certification Bundle',
  [PLANS.ECPPT_PREMIUM]: 'INE eCPPT Certification Bundle',
  [PLANS.EWPTX_PREMIUM]: 'INE eWPTX Certification Bundle',
  [PLANS.EMAPT_PREMIUM]: 'INE eMAPT Certification Bundle',
  [PLANS.ECTHP_PREMIUM]: 'INE eCTHP Certification Bundle',
  [PLANS.EDA_PREMIUM]: 'INE eEDA Certification Bundle',
  [PLANS.EDA_PREMIUM_CONT]: 'INE eEDA Continuation Bundle',
  [PLANS.ESOC_PREMIUM_CONT]: 'INE eSOC Continuation Bundle',
  [PLANS.EAIS_PREMIUM_CONT]: 'INE eAIS Continuation Bundle',
  [PLANS.PTA]: 'INE Premium Defense Bundle',
  [PLANS.SKILL_DIVE_ANNUAL]: 'INE Skill Dive Annual Plan',
  [PLANS.SKILL_DIVE_PTA]: 'INE Skill Dive PTA Plan',
  [PLANS.INE_TCS_BUNDLE]: 'INE Train, Certify, Save Bundle',
  [PLANS.BLKFRIDAY]: 'INE Black Friday Premium Bundle',
  [String(VOUCHERS.ICCA)]: 'INE ICCA Certification',
  [String(VOUCHERS.EJPT)]: 'INE eJPT Certification',
  [String(VOUCHERS.EDA)]: 'INE eEDA Certification',
  [String(VOUCHERS.ECDFP)]: 'INE ECDFP Certification',
  [String(VOUCHERS.ECIR)]: 'INE ECIR Certification',
  [String(VOUCHERS.EJDS)]: 'INE EJDS Certification',
  [String(VOUCHERS.ECPPT)]: 'INE eCPPT Certification',
  [String(VOUCHERS.ECTHP)]: 'INE eCTHP Certification',
  [String(VOUCHERS.EMAPT)]: 'INE eMAPT Certification',
  [String(VOUCHERS.EWPTX)]: 'INE eWPTX Certification',
  [String(VOUCHERS.EWPT)]: 'INE eWPT Certification',
  [String(VOUCHERS.ECPPT3)]: 'INE eCPPT Certification',
  [String(VOUCHERS.ESOC)]: 'INE eSOC Certification',
  [String(VOUCHERS.EAIS)]: 'INE eAIS Certification',
  [BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_ONE]: 'INE Black Friday Bundle One',
  [BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_TWO]: 'INE Black Friday Bundle Two',
  [BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_THREE]: 'INE Black Friday Bundle Three',
  [LEAP_YEAR_PROMO_PRODUCT_BUNDLES.INE_LEAP_YEAR_BUNDLE]: 'INE Leap Year Bundle',
  [TCS_PROMO_PRODUCT_BUNDLES.INE_TCS_BUNDLE]: 'INE Train, Certify, Save Bundle',
  [SUMMER_SAVINGS_BUNDLE.INE_SUMMER_SAVINGS_BUNDLE]: 'INE Summer Savings Bundle',
}

const descriptorPatterns: Record<string, RegExp> = {
  plan: /(plan|subscription|bundle|voucher|certification)/i,
  bundle: /bundle/i,
  certificate: /(certification|voucher|exam)/i,
  live_virtual_training: /(training|bootcamp|live)/i,
  extension: /extension/i,
  addon: /add-?on/i,
}

const descriptorLabels: Record<string, string> = {
  plan: 'Plan',
  bundle: 'Bundle',
  certificate: 'Certification',
  live_virtual_training: 'Live Training',
  extension: 'Extension',
  addon: 'Add-On',
}

type MaybeProduct = Partial<IProductExtended> & { merchant_id?: string }

type TitleInput = {
  product?: MaybeProduct | null
  routeName?: string | null | undefined
}

function humanizeMerchantId(merchantId: string): string {
  return merchantId
    .split(/[-_]/)
    .filter(Boolean)
    .map(segment =>
      segment.length <= 4
        ? segment.toUpperCase()
        : segment.charAt(0).toUpperCase() + segment.slice(1)
    )
    .join(' ')
}

function prefixWithIne(name: string): string {
  return /^INE\b/i.test(name) ? name : `INE ${name}`
}

function applyDescriptor(name: string, type?: string): string {
  if (!type) return name
  const normalizedType = type as keyof typeof descriptorPatterns
  const pattern = descriptorPatterns[normalizedType]
  const label = descriptorLabels[normalizedType]

  if (!pattern || !label) {
    return name
  }

  if (pattern.test(name)) {
    return name
  }

  return `${name} ${label}`
}

function normalizeProductType(
  type?: string | null,
  merchantId?: string
): keyof typeof descriptorPatterns | undefined {
  if (type && descriptorPatterns[type]) {
    return type as keyof typeof descriptorPatterns
  }

  if (!merchantId) return undefined

  if (/cert/.test(merchantId)) return 'certificate'
  if (/bundle/.test(merchantId)) return 'bundle'
  if (/live|lvt/.test(merchantId)) return 'live_virtual_training'
  if (/extension/.test(merchantId)) return 'extension'

  return undefined
}

// Define a local type to include all possible name properties
type MaybeProductWithNames = MaybeProduct & {
  seoTitle?: string
  metaTitle?: string
  meta_title?: string
  label?: string
  nameToShow?: string
  nameBundle?: string
}

function pickProductName(product?: MaybeProductWithNames | null): string | null {
  if (!product) return null

  const nameCandidates = [
    product.seoTitle,
    product.metaTitle,
    product.meta_title,
    product.label,
    product.nameToShow,
    product.nameBundle,
    product.nameLong,
    product.nameShort,
    product.name,
  ]

  const found = nameCandidates.find(value => typeof value === 'string' && value.trim().length)
  if (found) {
    return found.trim()
  }

  if (product.merchant_id) {
    return humanizeMerchantId(product.merchant_id)
  }

  return null
}

function formatProductTitle(product: MaybeProduct): string | null {
  const merchantId = product?.merchant_id
  if (!merchantId && !product) {
    return null
  }

  const overrideTitle = merchantId ? PRODUCT_TITLE_OVERRIDES[merchantId] : undefined
  if (overrideTitle) {
    return overrideTitle
  }

  const baseName = pickProductName(product)
  if (!baseName) {
    return null
  }

  const type = normalizeProductType(product?.product_type, merchantId)
  const labeledName = applyDescriptor(baseName, type)

  return prefixWithIne(labeledName)
}

export function buildCheckoutTitle({ product, routeName }: TitleInput): string {
  const routeOverride = routeName ? ROUTE_TITLE_OVERRIDES[routeName] : undefined

  if (product) {
    const normalizedTitle = formatProductTitle(product)
    if (normalizedTitle) {
      return normalizedTitle + DEFAULT_SUFFIX
    }
  }

  if (routeOverride) {
    return routeOverride
  }

  return DEFAULT_CHECKOUT_TITLE
}
